/**
 * API Configuration and Axios Instance
 *
 * Centralized API configuration with:
 * - Base URL configuration
 * - Request/response interceptors
 * - Auth token injection
 * - Error handling
 */

// API Base URL - can be configured per environment
export const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Get authentication headers
 * @param {Object} currentUser - Firebase user object
 * @returns {Object} Headers object with auth token
 */
export async function getAuthHeaders(currentUser) {
    const headers = {
        'Content-Type': 'application/json',
        'X-Frontend-Source': 'react-frontend',
    };

    if (currentUser) {
        try {
            const token = await currentUser.getIdToken();
            headers['Authorization'] = `Bearer ${token}`;
        } catch (e) {
            console.error('Failed to get ID token:', e);
            // Fall back to dev header
            headers['X-Dev-User-Id'] = 'tim@vortx.us';
        }
    } else {
        headers['X-Dev-User-Id'] = 'tim@vortx.us';
    }

    return headers;
}

/**
 * Make an API request with automatic auth handling
 * @param {string} url - API endpoint
 * @param {Object} options - Fetch options
 * @param {Object} currentUser - Firebase user object (optional)
 * @returns {Promise<Response>}
 */
export async function apiRequest(url, options = {}, currentUser = null) {
    // Require authentication for all API requests
    if (!currentUser) {
        console.warn('[API] Request blocked: No authenticated user', url);
        throw new Error('Authentication required. Please log in.');
    }

    const headers = await getAuthHeaders(currentUser);
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    const response = await fetch(fullUrl, {
        ...options,
        headers: {
            ...headers,
            ...options.headers,
        },
    });

    return response;
}

/**
 * Make a GET request
 */
export async function apiGet(url, currentUser = null) {
    return apiRequest(url, { method: 'GET' }, currentUser);
}

/**
 * Make a POST request
 */
export async function apiPost(url, data, currentUser = null) {
    return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(data),
    }, currentUser);
}

/**
 * Make a PUT request
 */
export async function apiPut(url, data, currentUser = null) {
    return apiRequest(url, {
        method: 'PUT',
        body: JSON.stringify(data),
    }, currentUser);
}

/**
 * Make a PATCH request
 */
export async function apiPatch(url, data, currentUser = null) {
    return apiRequest(url, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, currentUser);
}

/**
 * Make a DELETE request
 */
export async function apiDelete(url, currentUser = null) {
    return apiRequest(url, { method: 'DELETE' }, currentUser);
}

/**
 * Handle API response with error extraction
 */
export async function handleApiResponse(response) {
    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.error || data.message || `Request failed: ${response.status}`);
        error.status = response.status;
        error.data = data;
        throw error;
    }

    return data;
}

export default {
    getAuthHeaders,
    apiRequest,
    apiGet,
    apiPost,
    apiPut,
    apiPatch,
    apiDelete,
    handleApiResponse,
    API_BASE_URL,
};
